import { motion } from "framer-motion";
import { useT } from "../../lib/i18n";

/**
 * Cinematic video presentation, framed inside a stylised smartphone.
 *
 *   ╭─────────────╮  Phone bezel:  rounded ~3rem, dark glass.
 *   │  ▢  notch   │  Notch:        camera + dynamic island feel.
 *   │             │  Side buttons: power (R), volume (L).
 *   │   showreel  │  Screen:       9:16 portrait — plays /videos/showreel.mp4
 *   ╰─────────────╯
 *
 *   The whole device tilts subtly (3D perspective) and casts a warm
 *   ambient glow + reflection, like a phone held in front of you in a
 *   dark room. Editorial copy sits to the left of the device.
 */

const SHOWREEL_SRC = "/videos/showreel.mp4";
const SHOWREEL_POSTER = "/videos/showreel-poster.jpg";

export const VideoPresentation = () => {
  const t = useT();

  return (
    <section
      data-testid="video-presentation"
      className="relative bg-ink overflow-hidden py-20 md:py-32"
    >
      {/* Ambient warm glow */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at 70% 50%, rgba(255,170,90,0.10) 0%, rgba(0,0,0,0) 55%), radial-gradient(ellipse at 30% 30%, rgba(184,169,154,0.06) 0%, rgba(0,0,0,0) 60%), linear-gradient(180deg, #0d0d0d 0%, #050505 100%)",
        }}
      />
      {/* Subtle film grain overlay */}
      <div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none opacity-[0.08] mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='200' height='200'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9'/></filter><rect width='100%25' height='100%25' filter='url(%23n)' opacity='0.7'/></svg>\")",
        }}
      />

      <div className="container-x relative grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
        {/* LEFT — editorial copy */}
        <div className="lg:col-span-5 order-2 lg:order-1 text-bone">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="flex items-center gap-3 mb-5">
              <span className="h-px w-10 bg-stone/60" />
              <div className="eyebrow text-stone">{t("video.eyebrow")}</div>
            </div>
            <h2 className="font-display text-4xl md:text-6xl lg:text-7xl leading-[0.95]">
              {t("video.headlineA")}{" "}
              <span className="italic text-stone">{t("video.headlineB")}</span>.
            </h2>
            <p className="mt-6 text-bone/65 text-base md:text-lg leading-relaxed max-w-md">
              {t("video.intro")}
            </p>
            <div className="mt-8 inline-flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase text-bone/55">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              {t("video.live")}
            </div>
          </motion.div>
        </div>

        {/* RIGHT — phone mockup (shifted slightly left so the 9:16 frame
            is never clipped at the viewport edge). */}
        <div className="lg:col-span-7 order-1 lg:order-2 flex justify-center lg:justify-center lg:pr-8">
          <motion.div
            initial={{ opacity: 0, scale: 0.94, rotateY: 20 }}
            whileInView={{ opacity: 1, scale: 1, rotateY: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
            style={{ perspective: "1600px" }}
          >
            <Phone />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

/* ─────────── Stylised iPhone-like device ───────────────────────────
   Outer chassis is locked to a 9:16 aspect ratio so the showreel video
   (1080×1920) fills the inner screen edge-to-edge with no cropping. */
const Phone = () => (
  <div
    data-testid="phone-mock"
    className="phone-tilt relative"
    style={{
      width: "min(86vw, 360px)",
      aspectRatio: "9 / 16",
    }}
  >
    {/* Floor reflection — warm halo under the device */}
    <div
      aria-hidden="true"
      className="absolute left-1/2 -translate-x-1/2 -bottom-16 w-[120%] h-32 pointer-events-none opacity-50 blur-2xl"
      style={{
        background:
          "radial-gradient(ellipse at center, rgba(255,170,90,0.45) 0%, rgba(0,0,0,0) 70%)",
      }}
    />

    {/* Outer chassis — brushed dark aluminium */}
    <div
      className="absolute inset-0 rounded-[2.6rem] md:rounded-[3rem]"
      style={{
        background:
          "linear-gradient(135deg, #1a1a1c 0%, #0a0a0b 35%, #1f1f22 70%, #08080a 100%)",
        boxShadow:
          "0 60px 100px -30px rgba(0,0,0,0.85), 0 25px 50px -20px rgba(255,170,90,0.18), inset 0 0 0 1.5px rgba(255,255,255,0.08), inset 0 -3px 8px rgba(0,0,0,0.6)",
      }}
    />

    {/* Volume buttons (left) */}
    <div className="absolute -left-[3px] top-[18%] w-[3px] h-10 rounded-l-sm bg-gradient-to-b from-[#3a3a3d] to-[#1c1c1e]" />
    <div className="absolute -left-[3px] top-[27%] w-[3px] h-16 rounded-l-sm bg-gradient-to-b from-[#3a3a3d] to-[#1c1c1e]" />
    {/* Power button (right) */}
    <div className="absolute -right-[3px] top-[24%] w-[3px] h-20 rounded-r-sm bg-gradient-to-b from-[#3a3a3d] to-[#1c1c1e]" />

    {/* Inner screen frame */}
    <div className="absolute inset-[10px] md:inset-[11px] rounded-[2.1rem] md:rounded-[2.4rem] bg-black overflow-hidden">
      <div className="relative w-full h-full">
        <video
          src={SHOWREEL_SRC}
          poster={SHOWREEL_POSTER}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          className="absolute inset-0 w-full h-full object-cover bg-black"
          data-testid="showreel-video"
        />

        {/* Top status bar (time + indicators) */}
        <div className="absolute top-0 left-0 right-0 h-8 md:h-9 flex items-center justify-between px-5 md:px-6 text-[10px] md:text-[11px] tracking-wide font-medium text-white/90 z-20">
          <span>9:41</span>
          <span className="flex items-center gap-1.5">
            {/* Cellular */}
            <svg width="14" height="9" viewBox="0 0 14 9" fill="currentColor">
              <rect x="0" y="6" width="2" height="3" rx="0.5" />
              <rect x="3" y="4" width="2" height="5" rx="0.5" />
              <rect x="6" y="2" width="2" height="7" rx="0.5" />
              <rect x="9" y="0" width="2" height="9" rx="0.5" />
            </svg>
            {/* Wifi */}
            <svg width="13" height="9" viewBox="0 0 13 9" fill="currentColor">
              <path d="M6.5 7.5a1 1 0 110 2 1 1 0 010-2zm0-3.2c1 0 1.95.38 2.66 1.07a.5.5 0 11-.7.7A2.79 2.79 0 006.5 5.3a2.79 2.79 0 00-1.96.78.5.5 0 01-.7-.7A3.79 3.79 0 016.5 4.3zm0-2.6c1.74 0 3.4.67 4.66 1.85a.5.5 0 11-.68.74A5.83 5.83 0 006.5 2.7a5.83 5.83 0 00-3.98 1.59.5.5 0 11-.68-.74A6.83 6.83 0 016.5 1.7z" />
            </svg>
            {/* Battery */}
            <svg width="22" height="10" viewBox="0 0 22 10" fill="none">
              <rect x="0.5" y="0.5" width="18" height="9" rx="2" stroke="currentColor" strokeOpacity="0.6" />
              <rect x="2" y="2" width="14" height="6" rx="1" fill="currentColor" />
              <rect x="20" y="3.5" width="1.5" height="3" rx="0.5" fill="currentColor" fillOpacity="0.6" />
            </svg>
          </span>
        </div>

        {/* Dynamic island / notch */}
        <div className="absolute top-1.5 md:top-2 left-1/2 -translate-x-1/2 w-[34%] h-6 md:h-7 rounded-full bg-black z-30 flex items-center justify-end pr-2.5">
          <span className="w-1.5 h-1.5 rounded-full bg-[#1f2024] ring-1 ring-[#444]" />
        </div>

        {/* Bottom gradient + caption */}
        <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/85 via-black/30 to-transparent pointer-events-none" />
        <div className="absolute bottom-7 md:bottom-9 left-5 right-5 text-white z-20">
          <div className="text-[9px] tracking-[0.32em] uppercase opacity-80 mb-1.5">
            @romain.bruneau · Reel
          </div>
          <div className="font-display italic text-lg md:text-xl leading-tight drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)]">
            Sélection cinématique
          </div>
        </div>

        {/* Subtle screen glare */}
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none mix-blend-overlay opacity-40"
          style={{
            background:
              "linear-gradient(115deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0) 35%, rgba(255,255,255,0) 75%, rgba(255,255,255,0.08) 100%)",
          }}
        />
      </div>
    </div>

    {/* Outer rim highlight */}
    <div
      aria-hidden="true"
      className="absolute inset-0 rounded-[2.6rem] md:rounded-[3rem] pointer-events-none"
      style={{
        boxShadow:
          "inset 0 1px 0 rgba(255,255,255,0.15), inset 0 -1px 0 rgba(0,0,0,0.45)",
      }}
    />
  </div>
);
